/**
 * QUÉ FALTA Y QUÉ SE PREGUNTA (CQ2). Lo calcula el CÓDIGO, no el modelo: el
 * asistente (CQ4) solo pone el texto, en el idioma de quien pregunta.
 *
 * El guion (PLAN §3) va en tres niveles y cada pregunta se salta si el dato ya
 * existe:
 *   - UNA VEZ POR EMPRESA: razón social y NIF, REA, dirección, titular, GIP y
 *     asesor con ROPO, carnés, máquinas, riego y protección, SIGPAC de cada nave,
 *     captaciones y zonas;
 *   - POR CAMPAÑA: periodo, nº de registro y plaga POR PRODUCTO (una pregunta
 *     por producto, no por línea), eficacia en bloque, equipo o aplicador cuando
 *     solo hay uno dado de alta, la máquina y el aplicador DEDUCIDOS de los partes
 *     viejos (CU2, se confirman en bloque), los SI/NO del 3.2 al 3.5 y análisis;
 *   - POR EVENTO: lo que no se agrupa (el equipo de un parte cuando hay varios,
 *     una dosis en envases, un parte sin superficie ni cultivo).
 *
 * Cada pregunta dice cuánto desbloquea (`cuenta`) y, cuando el código tiene una
 * respuesta probable (COSTAR → 22060; goteo e invernadero; NO en el 3.2), la
 * trae como `propuesta`: se contesta con un «sí», nunca se aplica sola.
 */
import type { AplicadorEntrada, DecisionesCuaderno, EquipoEntrada } from "./entrada";
import type { DatosLegalesRow } from "./datosLegales";
import { GRUPO_DE, ORDEN_GUION, type ClavePregunta, type Pregunta } from "./modeloOficial";
import type { MetaFila31, Tratamiento31 } from "./rows31";
import { checkNif, checkRoma, checkRopo, isValidCodigoPostal } from "./validators";

export interface EstadoParaPreguntas {
  legal: DatosLegalesRow | null | undefined;
  /** Aplicadores y máquinas que aparecen en los partes del cuaderno. */
  aplicadores: AplicadorEntrada[];
  equipos: EquipoEntrada[];
  aplicadoresEmpresa: AplicadorEntrada[];
  equiposEmpresa: EquipoEntrada[];
  /** Naves con parcela en el 2.1 y sin recinto SIGPAC confirmado. */
  navesSinSigpac: string[];
  /** Fincas del cuaderno sin datos del 2.2 guardados. */
  fincasSin22: string[];
  filas31: Tratamiento31[];
  meta31: MetaFila31[];
  decisiones: DecisionesCuaderno | undefined;
  periodo: { desde: string; hasta: string; etiqueta: string };
}

const vacio = (v: unknown) => !String(v ?? "").trim();

/** ¿El ROMA de una máquina vale? Exenta (mochila < 100 L) no lleva nº y está bien. */
export const romaExenta = (roma: string | null | undefined) => /^\s*exent/i.test(roma ?? "");

export function siguientesPreguntas(s: EstadoParaPreguntas): Pregunta[] {
  const out: Pregunta[] = [];
  const add = (clave: ClavePregunta, p: Omit<Pregunta, "clave" | "grupo">) => out.push({ clave, grupo: GRUPO_DE[clave], ...p });
  const L = s.legal ?? {};

  // ── Una vez por empresa ──────────────────────────────────────────────────
  if (vacio(L.razon_social) || !checkNif(L.nif).ok) add("razon_social_nif", { cuenta: 1 });
  if (vacio(L.rea_nacional)) add("rea", { cuenta: 1 });
  if (vacio(L.direccion) || vacio(L.localidad) || !isValidCodigoPostal(L.codigo_postal) || vacio(L.provincia)) add("direccion", { cuenta: 1 });
  if (vacio(L.titular_nombre) || !checkNif(L.titular_nif).ok) add("titular", { cuenta: 1 });
  // D18: el asesor solo cuenta con ROPO; el tipo de explotación en GIP siempre.
  if (vacio(L.gip_tipo) || (!vacio(L.asesor_nombre) && !checkRopo(L.asesor_ropo).ok)) add("gip_asesor", { cuenta: 1 });

  const sinCarne = s.aplicadores.filter((a) => !checkRopo(a.ropo).ok || !a.carne);
  if (sinCarne.length) add("carnes", { cuenta: sinCarne.length });
  const maquinasMal = s.equipos.filter((e) => !romaExenta(e.roma) && !checkRoma(e.roma).ok);
  if (maquinasMal.length || (s.equiposEmpresa.length === 0 && s.filas31.length > 0)) add("maquinas", { cuenta: Math.max(1, maquinasMal.length) });

  if (vacio(L.riego_defecto) || vacio(L.proteccion_defecto)) add("riego_proteccion", { cuenta: 1, propuesta: { valor: "LOC · INV", detalle: "goteo e invernadero" } });
  if (s.navesSinSigpac.length) add("sigpac", { cuenta: s.navesSinSigpac.length, naves: s.navesSinSigpac });
  if (s.fincasSin22.length) add("captaciones_zonas", { cuenta: s.fincasSin22.length, fincas: s.fincasSin22, propuesta: { valor: "NO" } });

  // ── Por campaña ──────────────────────────────────────────────────────────
  if (!s.decisiones?.periodoConfirmado) add("periodo", { cuenta: 1, propuesta: { valor: s.periodo.etiqueta, detalle: `${s.periodo.desde}/${s.periodo.hasta}` } });

  // Nº de registro y plaga: POR PRODUCTO.
  const porProducto = (campo: string) => {
    const m = new Map<string, { producto: string; lineas: string[]; partes: Set<string>; propuesta: MetaFila31["propuestaRegistro"] }>();
    s.meta31.forEach((meta, i) => {
      // Un nº «sin comprobar» (sin catálogo, L7) no se pregunta: el dato está, falta comprobarlo.
      if (!meta.huecos.some((h) => h.campo === campo && h.motivo !== "registro_sin_comprobar")) return;
      const nombre = s.filas31[i].producto.nombreComercial;
      const k = nombre.toLocaleLowerCase("es");
      const g = m.get(k) ?? { producto: nombre, lineas: [], partes: new Set<string>(), propuesta: meta.propuestaRegistro };
      g.lineas.push(...meta.lineas);
      meta.partes.forEach((p) => g.partes.add(p));
      m.set(k, g);
    });
    return [...m.values()].sort((a, b) => b.lineas.length - a.lineas.length);
  };
  for (const g of porProducto("numeroRegistro")) {
    add("numero_registro", {
      cuenta: g.lineas.length,
      producto: g.producto,
      lineas: g.lineas,
      partes: [...g.partes],
      ...(g.propuesta ? { propuesta: { valor: g.propuesta.num, detalle: g.propuesta.nombre } } : {}),
    });
  }
  for (const g of porProducto("problema")) add("plaga", { cuenta: g.lineas.length, producto: g.producto, lineas: g.lineas, partes: [...g.partes] });

  // D17: eficacia «Buena» por defecto y se confirman las excepciones, en bloque.
  const eficaciaPorConfirmar = s.meta31.filter((m) => m.huecos.some((h) => h.campo === "eficacia") || m.origenes.eficacia === "defecto");
  if (eficaciaPorConfirmar.length) {
    add("eficacia", { cuenta: eficaciaPorConfirmar.length, partes: [...new Set(eficaciaPorConfirmar.flatMap((m) => m.partes))], propuesta: { valor: "buena" } });
  }

  // Equipo y aplicador: con UNO dado de alta se propone para todas; con varios, parte a parte.
  const sinCampo = (campo: string) => s.meta31.filter((m) => m.huecos.some((h) => h.campo === campo));
  const porPersonaOMaquina = (campo: "equipo" | "aplicador", deEmpresa: { id: string; nombre: string }[], campana: ClavePregunta, evento: ClavePregunta) => {
    const faltan = sinCampo(campo);
    if (!faltan.length) return;
    const partes = [...new Set(faltan.flatMap((m) => m.partes))];
    if (deEmpresa.length === 1) {
      add(campana, { cuenta: partes.length, partes, propuesta: { valor: deEmpresa[0].id, detalle: deEmpresa[0].nombre } });
    } else if (deEmpresa.length > 1) {
      for (const p of partes) add(evento, { cuenta: 1, partes: [p] });
    }
    // Sin ninguna dada de alta, lo pregunta el nivel de empresa («máquinas», «carnés»).
  };
  porPersonaOMaquina("equipo", s.equiposEmpresa.map((e) => ({ id: e.id, nombre: e.descripcion })), "equipo", "equipo_parte");
  porPersonaOMaquina("aplicador", s.aplicadoresEmpresa.map((a) => ({ id: a.id, nombre: a.nombre })), "aplicador", "aplicador_parte");
  if (s.aplicadoresEmpresa.length === 0 && sinCampo("aplicador").length) add("carnes", { cuenta: 1 });

  /* CU2: máquina o aplicador DEDUCIDOS (el parte no los guardó): se imprimen, pero se
     confirman en BLOQUE, una vez (la tarjeta los guarda en cada parte). */
  const deducidas = s.meta31.filter((m) => m.origenes.aplicador === "deducido" || m.origenes.equipo === "deducido");
  if (deducidas.length) {
    const partes = [...new Set(deducidas.flatMap((m) => m.partes))];
    const cuantos = (campo: "aplicador" | "equipo") => new Set(s.meta31.filter((m) => m.origenes[campo] === "deducido").flatMap((m) => m.partes)).size;
    add("confirmar_deducidos", { cuenta: partes.length, partes, propuesta: { valor: "sí", detalle: `aplicador en ${cuantos("aplicador")}, máquina en ${cuantos("equipo")}` } });
  }

  const aplica = s.decisiones?.aplica ?? {};
  if ((["3.2", "3.3", "3.4", "3.5"] as const).some((k) => aplica[k] == null)) add("si_no_3_2_3_5", { cuenta: 4, propuesta: { valor: "NO" } });
  if (s.decisiones?.huboAnalisis == null) add("analisis", { cuenta: 1, propuesta: { valor: "NO" } });

  // ── Por evento (lo que no se agrupa) ─────────────────────────────────────
  for (const m of s.meta31) {
    const h = (campo: string) => m.huecos.find((x) => x.campo === campo);
    const d = h("dosis");
    if (d && d.motivo !== "sin_superficie") add("dosis_parte", { cuenta: 1, partes: m.partes, lineas: m.lineas });
    if (h("superficieHa")) add("superficie_parte", { cuenta: 1, partes: m.partes });
    if (h("especie")) add("cultivo_parte", { cuenta: 1, partes: m.partes });
  }

  // Sin repetir: la misma clave y los mismos partes se juntan (varias filas de un parte).
  const vistos = new Map<string, Pregunta>();
  for (const p of out) {
    const k = `${p.clave}|${p.producto ?? ""}|${(p.partes ?? []).join(",")}`;
    const prev = vistos.get(k);
    if (prev) {
      if (p.clave.endsWith("_parte")) continue;
      prev.cuenta += p.cuenta;
    } else vistos.set(k, { ...p });
  }
  const orden = new Map(ORDEN_GUION.map((c, i) => [c, i]));
  return [...vistos.values()].sort((a, b) => (orden.get(a.clave) ?? 99) - (orden.get(b.clave) ?? 99));
}
